// @flow
import moment from "moment";
import { propComparator } from "../../../global/functions/StringOperations";
import { buildLicenceItemJson } from "../companies/CompileCompanytoJson";

export function buildQuoteItemJson(quote: *): * {
  let json = {};
  json.id = quote.id;
  json.createDateOrigin = quote.auditCreationDate;
  json.createDate = moment
    .utc(quote.auditCreationDate)
    .local()
    .format("DD/MMM/YYYY hh:mm A");
  json.updateDateOrigin = quote.lastUpdatedDate;
  json.updateDate = quote.lastUpdatedDate
    ? moment.utc(quote.lastUpdatedDate).local().format("DD/MMM/YYYY hh:mm A")
    : "";

  json.company_name = quote.quotedCompanyName;
  json.quotedCompany = quote.quotedCompany;
  json.projectId = quote.project && quote.project.id;

  let authorised = null;
  if (quote.authorisedReseller === true) {
    authorised = 1;
  } else {
    authorised = -1;
  }
  json.authorised = authorised;

  json.quotedRetailPrice = quote.quotedRetailPrice;
  json.quotedDistributerPrice = quote.quotedDistributerPrice;
  json.quotedResellerPrice = quote.quotedResellerPrice;

  json.licenceType = quote.licenceType ? quote.licenceType : "LONGTERM";
  json.createdCompanyName = quote.company && quote.company.name;
  json.createdCompanyUid = quote.company && quote.company.uuid;
  json.createdCompanyId = quote.company && quote.company.id;
  json.createdCompanyType = quote.company && quote.company.companyType;
  json.createdCompanyAuthority =
    quote.company && quote.company.inheritAuthority;

  let parts = [];
  const quotedProductParts =
    quote &&
    quote.quotedProducts.edges &&
    quote.quotedProducts.edges[0].node &&
    quote.quotedProducts.edges[0].node.quotedProductParts.edges;
  if (quotedProductParts) {
    quotedProductParts.sort(propComparator("node", false, "part", "orderId"));

    for (let i = 0; i < quotedProductParts.length; i++) {
      parts.push({
        quantity: quotedProductParts[i].node.quantity,
        quotedRetailPrice: quotedProductParts[i].node.quotedRetailPrice,
        quotedDistributerPrice:
          quotedProductParts[i].node.quotedDistributerPrice,
        quotedResellerPrice: quotedProductParts[i].node.quotedResellerPrice,
        id: quotedProductParts[i].node.id,
        ...CompileDatabaseItemtoJsItem(quotedProductParts[i].node.part)
      });
    }
  }
  json.parts = parts;

  json.tierId = quote.tier && quote.tier.id;
  json.tierName = quote.tier && quote.tier.name;

  json.productSheetId = quote.productSheet && quote.productSheet.id;
  json.productSheetName = quote.productSheet && quote.productSheet.description;

  if (quote.licence) {
    json.licence = buildLicenceItemJson(quote.licence, false, true);
  }

  json.existingExpiryDateOrigin = quote.existingExpiryDate;
  json.existingExpiryDate = moment
    .utc(quote.existingExpiryDate)
    .local()
    .format("DD/MMM/YYYY");
  json.newStartDateOrigin = quote.newStartDate;
  json.newStartDate = moment
    .utc(quote.newStartDate)
    .local()
    .format("DD/MMM/YYYY");
  json.newExpiryDateOrigin = quote.newExpiryDate;
  json.newExpiryDate = moment
    .utc(quote.newExpiryDate)
    .local()
    .format("DD/MMM/YYYY");

  return json;
}

export function CompileDatabaseItemtoJsItem(item: *, sheetItem?: * = null): * {
  let json = {};
  json.partno = item.partNo;
  json.desctiotion = item.description;
  json.price = item.basePrice;
  json.type = item.priceType;
  json.notes = item.notes;
  json.min = item.minimumQuantity;
  json.max = item.maximumQuantity;
  json.hasRange = item.priceRanges.edges.length > 0;
  json.priceRanges = item.priceRanges.edges;
  json.licenceType = item.licenceType;
  json.orderId = item.orderId;

  // overwirte with customize prices
  if (sheetItem) {
    json.price = sheetItem.basePrice;
    json.min = sheetItem.minimumQuantity;
    json.max = sheetItem.maximumQuantity;
    json.hasRange = sheetItem.priceRanges.edges.length > 0;
    json.priceRanges = sheetItem.priceRanges.edges;
  }
  return json;
}

export function getQuoteNotes(
  note: string,
  min: number | null,
  max: number | null,
  textOnly?: boolean = false
): string {
  let notes = "";
  if (min !== null) {
    if (max !== null) {
      notes +=
        "Please specify a value from " +
        min.toString() +
        " to " +
        max.toString() +
        ".";
    } else {
      notes += "Please specify a minimum value of " + min.toString() + ".";
    }
  }
  if (note) {
    notes += " " + note.toString();
  }
  return textOnly ? note : notes;
}

export function checkItemPriceRange(
  value: number,
  min: number | null,
  max: number | null
): boolean {
  let price_check = false;
  if (!isNaN(value)) {
    if (min !== null) {
      if (parseInt(value) >= parseInt(min)) {
        if (max !== null) {
          if (parseInt(value) <= parseInt(max)) {
            price_check = true;
          }
        } else {
          price_check = true;
        }
      } else {
        price_check = false;
      }
    } else {
      price_check = true;
    }
  }
  return price_check;
}

export function calculateRetailPrice(
  type: string,
  value: number,
  hasRange: boolean,
  priceRanges: Object,
  price: number,
  partSum: number
): number {
  let retail_price = 0;
  if (type === "FIXED") {
    if (hasRange) {
      let base_price = parseFloat(price);
      for (let i = 0; i < priceRanges.length; i++) {
        let current_thresh = parseInt(priceRanges[i].node.minimumQuantity);
        if (parseInt(value) >= current_thresh) {
          base_price = parseFloat(priceRanges[i].node.price);
        } else {
          break;
        }
      }
      retail_price = base_price * parseFloat(value);
    } else {
      retail_price = parseFloat(price) * parseFloat(value);
    }
  } else if (type === "PERCENTAGE_OF_TOTAL_LICENCE_FEE") {
    retail_price = parseFloat(price) * parseFloat(value) * parseFloat(partSum);
  }
  return retail_price;
}

export function displayRetailPrice(
  checked: boolean,
  price: number | string,
  discount: number,
  shouldDisplay?: boolean = true
): string {
  if (!shouldDisplay) {
    return "-";
  } else if (checked) {
    return (
      "$" +
      ((parseFloat(price) * (100 - parseInt(discount))) / 100)
        .toFixed(2)
        .replace(/(\d)(?=(\d{3})+\.)/g, "$1,")
    );
  } else {
    return "";
  }
}

export function getRealPrice(
  checked: boolean,
  price: number | string,
  discount: number
): number {
  if (checked) {
    return (parseFloat(price) * (100 - parseInt(discount))) / 100;
  } else {
    return 0;
  }
}

export function totalPriceDescription(
  authorised: boolean,
  additioanlYears: number,
  type: string
): string {
  if (type === "SHORTTERM") {
    return "";
  } else {
    let years = 0;
    if (authorised) {
      years += 1;
    } else {
      years += 1;
    }
    years += parseInt(additioanlYears);
    return (
      "With Total " +
      years.toString() +
      " years Maintenance and Software Upgrade Services"
    );
  }
}

export function PriceFormat2Number(price: string): number {
  const val = parseFloat(price.replace("$", "").replace(",", ""));
  if (isNaN(val)) {
    return 0;
  } else {
    return val;
  }
}

export function getRetailSumFromTable(
  table: Array<Array<*>>,
  colIndex?: number
): number {
  let sum = 0;
  table.forEach((row: *): * => {
    sum += PriceFormat2Number(row[colIndex ? colIndex : 3]);
  });
  return sum;
}

export function getTotalPrices(
  sum: number,
  distributorDiscount: number,
  resellerDiscount: number
): Array<string> {
  return [
    displayRetailPrice(true, sum, 0),
    displayRetailPrice(true, sum, distributorDiscount),
    displayRetailPrice(true, sum, resellerDiscount)
  ];
}
